import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { NgxEchartsModule } from 'ngx-echarts';
import { EventsVsEscalationsChartData } from 'src/app/core/models/analytics/soc-models';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { MetricCardComponent } from 'src/app/core/components/metric-card/metric-card.component';
import { EChartsOption } from 'echarts';
import { GroupedBarChartConfig, GroupedBarChartData } from 'src/app/core/charts/configs';
import { ChartColors } from 'src/app/core/charts/utils/chart-colors';
import { SlaAdherenceResponse } from 'src/app/core/services/analytics.service';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { moreVerticalIcon, infoCircleIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
// Removed Kendo Dialog usage; no dialog imports needed here

@Component({
  selector: 'app-events-to-escalations-bar',
  standalone: true,
  imports: [
    NgxEchartsModule,
    MetricCardComponent,
    SkeletonModule,
    CommonModule,
    ButtonsModule,
    SVGIconModule,
    TooltipModule,
  ],
  templateUrl: './events-to-escalations-bar.component.html',
  styleUrl: './events-to-escalations-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventsToEscalationsBarComponent extends BaseComponent {
  public eventsVsEscalationsChartData = input<EventsVsEscalationsChartData>(new EventsVsEscalationsChartData());
  public slaAdherenceData = input<SlaAdherenceResponse>(new SlaAdherenceResponse());
  public organizationId = input<number | null>();
  public startDate = input<string | null>();
  public endDate = input<string | null>();
  public securityTool = input<string | undefined>();
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public infoCircleIcon: SVGIcon = infoCircleIcon;

  // Emit when the chart is clicked so parent can open dialog
  public chartClick = output<void>();

  // Output events to parent component - generic events for opening dialogs
  openAlertDialog = output<void>();

  // Computed signal to determine if SLA data is loading
  public isDataLoading = computed(() => {
    const data = this.slaAdherenceData();
    // Data is considered loading if all critical properties are 0 or null
    return (
      !data ||
      (data.sla_adherence_pct_current === 0 &&
        data.sla_adherence_pct_previous === 0 &&
        data.sla_violation_count_current === 0 &&
        data.sla_violation_count_previous === 0)
    );
  });

  // Handle SLA card click - emit event to parent
  public onSlaCardClick(): void {
    this.openAlertDialog.emit();
  }

  // Handle chart click - open dialog
  public onChartClick(event: any): void {
    this.chartClick.emit();
  }

  // No dialog state here; parent handles dialog rendering

  // ECharts configuration using centralized config
  chartOptions = computed((): EChartsOption => {
    const data = this.eventsVsEscalationsChartData().chart_data;

    if (!data || data.length === 0) {
      return this.getEmptyChartOptions();
    }

    const chartData: GroupedBarChartData = {
      categories: data.map((item) => {
        const date = new Date(item.date);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      series: [
        {
          name: 'Events',
          data: data.map((item) => item.events),
          color: ChartColors.SOC_PALETTE.EVENTS,
        },
        {
          name: 'Escalations',
          data: data.map((item) => item.escalations),
          color: ChartColors.SOC_PALETTE.ESCALATIONS,
        },
      ],
    };

    return GroupedBarChartConfig.create(chartData);
  });

  private getEmptyChartOptions(): EChartsOption {
    const emptyData: GroupedBarChartData = {
      categories: [],
      title: 'Events vs Escalations',
      series: [],
    };

    return GroupedBarChartConfig.create(emptyData, {
      tooltip: {
        formatter: () => 'No data available',
      },
    });
  }

  constructor() {
    super();
  }
}
